import { observer } from 'mobx-react-lite';
import { type ReactNode, createContext, useCallback, useContext } from 'react';

import { apiLogsStore } from '@/features/request-logger/api-logs.store';
import { type ApiLog } from '@/features/request-logger/api-logs.types';

interface ApiLogsInterceptorContextType {
  clearApiLogs: () => void;
  toggleApiLogsRealtimeStream: () => void;
  isApiLogsPaused: boolean;
  setSearchTerm: (searchTerm: string) => void;
  selectedApps: string[];
  selectedTags: string[];
  toggleApp: (app: string) => void;
  toggleTag: (tag: string) => void;
  availableApps: string[];
  availableTags: string[];
  filteredLogs: ApiLog[];
}

export const ApiInterceptorContext = createContext<
  ApiLogsInterceptorContextType | undefined
>(undefined);

export const ApiLogsInterceptorProvider = observer(
  ({ children }: { children: ReactNode }) => {
    const clearApiLogs = useCallback(() => {
      apiLogsStore.clearLogs();
    }, []);

    const toggleApiLogsRealtimeStream = useCallback(() => {
      apiLogsStore.setPaused(!apiLogsStore.paused);
    }, []);

    const toggleApp = (app: string) => {
      const prevApps = apiLogsStore.selectedApps;
      const index = prevApps.indexOf(app);
      if (index === -1) {
        apiLogsStore.setSelectedApps([...prevApps, app]);
      } else {
        const newApps = [...prevApps];
        newApps.splice(index, 1);
        apiLogsStore.setSelectedApps(newApps);
      }
    };

    const toggleTag = (tag: string) => {
      const prevTags = apiLogsStore.selectedTags;
      const index = prevTags.indexOf(tag);
      if (index === -1) {
        apiLogsStore.setSelectedTags([...prevTags, tag]);
      } else {
        const newTags = [...prevTags];
        newTags.splice(index, 1);
        apiLogsStore.setSelectedTags(newTags);
      }
    };

    return (
      <ApiInterceptorContext.Provider
        value={{
          clearApiLogs,
          toggleApiLogsRealtimeStream,
          isApiLogsPaused: apiLogsStore.paused,
          setSearchTerm: (v: string) => apiLogsStore.setSearchTerm(v),
          selectedApps: apiLogsStore.selectedApps,
          selectedTags: apiLogsStore.selectedTags,
          toggleApp,
          toggleTag,
          availableApps: apiLogsStore.availableApps,
          availableTags: apiLogsStore.availableTags,
          filteredLogs: apiLogsStore.filteredLogs,
        }}
      >
        {children}
      </ApiInterceptorContext.Provider>
    );
  }
);

export const useApiInterceptor = (): ApiLogsInterceptorContextType => {
  const context = useContext(ApiInterceptorContext);
  if (context === undefined) {
    throw new Error(
      'useApiInterceptor must be used within a ApiLogsInterceptorProvider'
    );
  }
  return context;
};
